import { toast } from 'react-hot-toast';
import ProcurementDocuments from '../../Common/ProcurementDocuments';
import { PlusIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useEffect, useRef, useState } from 'react';
import adminApi from '../../../services/adminApi';
import inventoryApi from '../../../services/inventoryApi';
import { assetsApi, taxConfigApi, budgetApi } from '../../../services/financeApi';
import SearchableCostCenterDropdown from '../../Common/SearchableCostCenterDropdown';
import SearchableSelect from '../../Common/SearchableSelect';
import { useLocation as useCurrentLocation } from '../../../contexts/LocationContext';

const GRNForm = ({ grn = null, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    receiptDate: new Date().toISOString().split('T')[0],
    supplier: null,
    selectedSupplier: null,
    store: null, // Required store/location field
    referenceNumber: '',
    purchaseOrder: null,
    deliveryNote: {
      number: '',
      date: ''
    },
    invoice: {
      number: '',
      date: '',
      amount: 0
    },
    lineItems: [],
    notes: '',
    withPO: true, // Toggle between with/without PO
    procurementType: 'INVENTORY',
    costCenter: '',
    costCenterLabel: ''
  });

  const [items, setItems] = useState([]);
  const [assets, setAssets] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [suppliersWithPOs, setSuppliersWithPOs] = useState([]);
  const [pendingPOs, setPendingPOs] = useState([]);
  const [stores, setStores] = useState([]);
  const { currentLocation } = useCurrentLocation() || {};
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPOs, setLoadingPOs] = useState(false);
  const [loadingStores, setLoadingStores] = useState(true);
  const [error, setError] = useState(null);

  const [ssclEnabled, setSsclEnabled] = useState(false);
  const [ssclRate, setSsclRate] = useState('2.5');
  const [vatEnabled, setVatEnabled] = useState(false);
  const [vatRate, setVatRate] = useState('');
  const [ssclAmount, setSsclAmount] = useState(0);
  const [vatAmount, setVatAmount] = useState(0);
  const [taxDefaults, setTaxDefaults] = useState({ ssclRate: '2.5', vatRate: '' });

  const isEditMode = !!(grn && grn._id);
  const docsRef = useRef(null);

  // ── Budget (GRN without a PO): each line's item expense account under the GRN's cost center ──
  // A GRN from a PO uses the PO's reservation, so only manual GRNs show and check budget here.
  const showBudget = !formData.withPO && formData.procurementType !== 'FIXED_ASSET';
  const [budgetByAccount, setBudgetByAccount] = useState({});
  const [reservedByThisGrn, setReservedByThisGrn] = useState({});
  const expenseAccountOf = (line) => {
    const id = line?.item?._id || line?.item;
    if (!id) return null;
    const it = items.find(i => String(i._id) === String(id));
    const acc = it?.accounting?.expenseAccount;
    return acc?.accountId ? { id: String(acc.accountId), label: acc.label || '' } : null;
  };
  const budgetAccountKey = showBudget
    ? [...new Set((formData.lineItems || []).map(l => expenseAccountOf(l)?.id).filter(Boolean))].sort().join(',')
    : '';
  useEffect(() => {
    if (!formData.costCenter || !budgetAccountKey) { setBudgetByAccount({}); return undefined; }
    let cancelled = false;
    const ids = budgetAccountKey.split(',');
    setBudgetByAccount(Object.fromEntries(ids.map(id => [id, { loading: true }])));
    Promise.all(ids.map(id => budgetApi.checkBudgetAvailability({ costCenter: formData.costCenter, ledgerAccountId: id, amount: 0 })
      .then(res => [id, res.data?.data || {}])
      .catch(err => [id, { error: err.response?.data?.message || 'No budget available' }])))
      .then(rows => { if (!cancelled) setBudgetByAccount(Object.fromEntries(rows)); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.costCenter, budgetAccountKey]);
  // Budget this saved GRN already holds (reserved when it was saved)
  useEffect(() => {
    if (!isEditMode || !showBudget) { setReservedByThisGrn({}); return undefined; }
    let cancelled = false;
    budgetApi.getDocumentReservation(grn._id)
      .then(res => {
        if (cancelled) return;
        const map = {};
        (res.data?.data?.lines || []).forEach(l => {
          if (String(l.costCenter) === String(formData.costCenter)) map[String(l.ledgerId)] = (map[String(l.ledgerId)] || 0) + (l.reserved || 0);
        });
        setReservedByThisGrn(map);
      })
      .catch(() => { if (!cancelled) setReservedByThisGrn({}); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, showBudget, grn?._id, formData.costCenter, budgetAccountKey]);
  // What this GRN needs on each expense account (accepted, else delivered quantity × price) — as the server reserves it
  const requestedByAccount = (formData.lineItems || []).reduce((acc, l) => {
    const a = expenseAccountOf(l);
    const qty = parseFloat(l.acceptedQuantity) || parseFloat(l.deliveredQuantity) || 0;
    if (a) acc[a.id] = (acc[a.id] || 0) + qty * (parseFloat(l.unitPrice) || 0);
    return acc;
  }, {});
  const budgetMoney = (n) => `Rs ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  useEffect(() => {
    loadInitialData();
  }, []);

  // Fetch SSCL and VAT rates from tax configurations on mount
  useEffect(() => {
    taxConfigApi.getTaxConfigs({ isActive: true, limit: 100 })
      .then(res => {
        const configs = res.data?.data?.taxConfigs || [];
        const ssclConfig = configs.find(c =>
          c.code === 'SSCL' || c.name?.toUpperCase().includes('SSCL')
        );
        const vatConfig = configs.find(c =>
          c.code === 'VAT' || c.name?.toUpperCase().includes('VAT')
        );
        const defaults = {
          ssclRate: ssclConfig ? String(ssclConfig.percentage) : '2.5',
          vatRate: vatConfig ? String(vatConfig.percentage) : '',
        };
        setTaxDefaults(defaults);
        if (!grn) {
          setSsclRate(defaults.ssclRate);
          setVatRate(defaults.vatRate);
        }
      })
      .catch(err => {
        console.warn('[GRNForm] could not fetch tax configs:', err.message);
      });
  }, []);

  // Separate effect to initialize form data after stores are loaded
  useEffect(() => {
    if (grn && stores.length > 0) {

      // Determine store ID — normalise to string for consistent comparison
      const rawStore = grn.warehouse || grn.store?._id || grn.store || grn.storeId || grn.location?._id || grn.location;
      const storeId = rawStore?._id ? String(rawStore._id) : (rawStore ? String(rawStore) : null);


      const initialFormData = {
        receiptDate: grn.receiptDate ? new Date(grn.receiptDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        supplier: grn.supplier?._id || grn.supplierId || null,
        selectedSupplier: grn.supplier || grn.selectedSupplier || null,
        store: storeId,
        referenceNumber: grn.referenceNumber || '',
        purchaseOrder: grn.purchaseOrder || null,
        deliveryNote: {
          number: grn.deliveryNote?.number || '',
          date: grn.deliveryNote?.date ? new Date(grn.deliveryNote.date).toISOString().split('T')[0] : ''
        },
        invoice: {
          number: grn.invoice?.number || '',
          date: grn.invoice?.date ? new Date(grn.invoice.date).toISOString().split('T')[0] : '',
          amount: grn.invoice?.amount || 0
        },
        lineItems: grn.lineItems?.map(grnItem => ({
          purchaseOrderItemId: grnItem.purchaseOrderItemId || null,
          item: grnItem.item || null,
          assetId: grnItem.assetId?._id || grnItem.assetId || null,
          serviceId: grnItem.serviceId?._id || grnItem.serviceId || null,
          assetCode: grnItem.assetCode || '',
          assetName: grnItem.assetName || '',
          description: grnItem.description || grnItem.item?.name || grnItem.assetName || '',
          orderedQuantity: grnItem.orderedQuantity || 0,
          receivedQuantity: grnItem.receivedQuantity || 0,
          deliveredQuantity: grnItem.deliveredQuantity || grnItem.pendingQuantity || 0,
          acceptedQuantity: grnItem.acceptedQuantity || grnItem.deliveredQuantity || grnItem.pendingQuantity || 0,
          rejectedQuantity: grnItem.rejectedQuantity || 0,
          unitPrice: grnItem.unitPrice || 0,
          condition: grnItem.condition || 'good',
          batchNumber: grnItem.batchNumber || '',
          expiryDate: grnItem.expiryDate ? new Date(grnItem.expiryDate).toISOString().split('T')[0] : '',
          manufacturingDate: grnItem.manufacturingDate ? new Date(grnItem.manufacturingDate).toISOString().split('T')[0] : '',
          notes: grnItem.notes || ''
        })) || [],
        notes: grn.notes || '',
        withPO: grn.withPO !== undefined ? grn.withPO : !!grn.purchaseOrder,
        procurementType: grn.procurementType || 'INVENTORY',
        costCenter: grn.costCenter || '',
        costCenterLabel: grn.costCenterLabel || ''
      };

      setFormData(initialFormData);

      // Prefer the GRN's own saved SSCL/VAT rates; fall back to the source PO, then tax config defaults
      const po = grn.purchaseOrder && typeof grn.purchaseOrder === 'object' ? grn.purchaseOrder : null;
      const hasOwnRates = (grn.totals?.ssclRate > 0) || (grn.totals?.vatRate > 0)
        || (grn.totals?.ssclAmount > 0) || (grn.totals?.vatAmount > 0);
      const rateSource = hasOwnRates ? grn : (po || grn);
      setSsclEnabled(rateSource.totals?.ssclAmount > 0 || rateSource.totals?.ssclRate > 0);
      setSsclRate(rateSource.totals?.ssclRate > 0 ? String(rateSource.totals.ssclRate) : taxDefaults.ssclRate);
      setVatEnabled(rateSource.totals?.vatAmount > 0 || rateSource.totals?.vatRate > 0);
      setVatRate(rateSource.totals?.vatRate > 0 ? String(rateSource.totals.vatRate) : taxDefaults.vatRate);

      // If navigating from PO, ensure the supplier is in the suppliersWithPOs list
      const navSupplier = grn.supplier || grn.selectedSupplier;
      if (navSupplier && navSupplier._id) {
        setSuppliersWithPOs(prev => {
          const exists = prev.some(s => String(s._id) === String(navSupplier._id));
          return exists ? prev : [navSupplier, ...prev];
        });
      }

      // If this GRN has a purchase order and supplier, load the pending POs for that supplier
      if (grn.purchaseOrder && (grn.supplier || grn.selectedSupplier)) {
        const supplierId = grn.supplier?._id || grn.supplier || grn.selectedSupplier?._id || grn.selectedSupplier;
        loadPendingPOs(supplierId);
      }
    }
  }, [grn, stores, taxDefaults]);

  // Effect to update line items with proper item details once items are loaded
  useEffect(() => {
    if (items.length > 0 && formData.lineItems.length > 0) {
      setFormData(prev => ({
        ...prev,
        lineItems: prev.lineItems.map(lineItem => {
          if (lineItem.item && (typeof lineItem.item === 'string' || !lineItem.item.name)) {
            // Find the full item details from the items array
            const itemId = typeof lineItem.item === 'string' ? lineItem.item : lineItem.item._id;
            const fullItem = items.find(item => item._id === itemId);
            if (fullItem) {
              return {
                ...lineItem,
                item: fullItem,
                description: lineItem.description || fullItem.name || fullItem.itemName || ''
              };
            }
          }
          return lineItem;
        })
      }));
    }
  }, [items, formData.lineItems.length]); // Only run when items load or line items count changes

  // Filter items by selected supplier in manual mode (fall back to all items when none are supplier-linked)
  useEffect(() => {
    if (formData.withPO) return;
    if (!formData.supplier) {
      setFilteredItems(items);
      return;
    }
    const filtered = items.filter(item => {
      const itemSupplierId = item.supplier?._id || item.supplier;
      return String(itemSupplierId) === String(formData.supplier);
    });
    setFilteredItems(filtered.length > 0 ? filtered : items);
  }, [formData.supplier, formData.withPO, items]);

  // Auto-calculate SSCL/VAT amounts
  useEffect(() => {
    const subtotal = formData.lineItems.reduce((sum, item) => sum + ((item.deliveredQuantity || 0) * (item.unitPrice || 0)), 0);
    const newSscl = ssclEnabled ? (subtotal * (parseFloat(ssclRate) || 0) / 100) : 0;
    const vatBase = subtotal + newSscl;
    const newVat = vatEnabled ? (vatBase * (parseFloat(vatRate) || 0) / 100) : 0;
    setSsclAmount(newSscl);
    setVatAmount(newVat);
  }, [formData.lineItems, ssclEnabled, ssclRate, vatEnabled, vatRate]);

  const loadInitialData = async () => {
    try {
      setLoadingStores(true);

      // Load basic data first
      const [itemsResponse, suppliersResponse] = await Promise.all([
        inventoryApi.getItems({ limit: 1000 }),
        inventoryApi.getSuppliers({ usableFor: 'goods', isActive: true, limit: 1000 })
      ]);

      // Load warehouses from locations
      let warehousesData = [];
      try {
        // Get locations filtered by warehouse type
        const warehousesResponse = await adminApi.getLocations({ type: 'warehouse' });
        warehousesData = warehousesResponse.data?.data || warehousesResponse.data || [];
      } catch (error) {
        console.error('Error loading warehouses, trying fallback without type filter:', error);
        try {
          const locationsResponse = await adminApi.getLocations();
          const allLocations = locationsResponse.data?.data || locationsResponse.data || [];
          warehousesData = allLocations.filter(location => location.type === 'warehouse');
        } catch (fallbackError) {
          console.error('Error loading locations as fallback:', fallbackError);
        }
      }

      // Load fixed assets for FIXED_ASSET GRNs (cross-service, best-effort)
      try {
        const assetsResponse = await assetsApi.getAll({ status: 'ACTIVE', limit: 200 });
        setAssets(assetsResponse.data?.data || assetsResponse.data || []);
      } catch (assetErr) {
        console.warn('Could not load fixed assets:', assetErr.message);
        setAssets([]);
      }

      // Handle nested data structure from API responses
      const itemsData = itemsResponse.data?.data || itemsResponse.data || [];
      const suppliersData = suppliersResponse.data?.data || suppliersResponse.data || [];

      setItems(itemsData);
      setFilteredItems(itemsData);
      setSuppliers(suppliersData);
      setStores(warehousesData); // Using warehouses for stores
      setWarehouses(warehousesData);

      // Try to load suppliers with pending POs (this might fail if endpoint doesn't exist)
      try {
        const suppliersWithPOsResponse = await inventoryApi.getSuppliersWithPendingPOs();
        const suppliersWithPOsData = suppliersWithPOsResponse.data?.data || suppliersWithPOsResponse.data || [];
        setSuppliersWithPOs(suppliersWithPOsData);
      } catch (err) {
        console.warn('Could not load suppliers with pending POs (endpoint may not exist):', err.message);
        // Fallback to regular suppliers
        setSuppliersWithPOs(suppliersData);
      }

    } catch (err) {
      console.error('Error loading initial data:', err);
      toast.error(err?.response?.data?.message || 'Failed to load data. Please refresh and try again.');
    } finally {
      setLoadingStores(false);
    }
  };

  const loadPendingPOs = async (supplierId) => {
    if (!supplierId) {
      setPendingPOs([]);
      return;
    }

    try {
      setLoadingPOs(true);

      try {
        const response = await inventoryApi.getPendingPOsBySupplier(supplierId);
        const posData = response.data?.data || response.data || [];

        // Ensure each PO has properly structured line items with populated item details
        let processedPOs = posData.map(po => ({
          ...po,
          lineItems: po.lineItems?.map(lineItem => ({
            ...lineItem,
            // Ensure the item object has the necessary fields
            item: {
              ...lineItem.item,
              _id: lineItem.item?._id || lineItem.item,
              name: lineItem.item?.name || 'Unknown Item',
              code: lineItem.item?.code || '',
              sku: lineItem.item?.sku || '',
              unit: lineItem.item?.unit || lineItem.item?.inventory?.unit || 'N/A'
            }
          })) || []
        }));

        // If there's a pre-selected PO (from navigation or edit mode), ensure it's in the list
        if (formData.purchaseOrder) {
          const currentPOId = typeof formData.purchaseOrder === 'object' 
            ? formData.purchaseOrder._id 
            : formData.purchaseOrder;
          const poExists = processedPOs.some(po => po._id === currentPOId);

          if (!poExists && typeof formData.purchaseOrder === 'object') {
            // If the PO is already an object (passed from navigation), add it directly
            const processedCurrentPO = {
              ...formData.purchaseOrder,
              lineItems: formData.purchaseOrder.lineItems?.map(lineItem => ({
                ...lineItem,
                item: {
                  ...lineItem.item,
                  _id: lineItem.item?._id || lineItem.item,
                  name: lineItem.item?.name || 'Unknown Item',
                  code: lineItem.item?.code || '',
                  sku: lineItem.item?.sku || '',
                  unit: lineItem.item?.unit || lineItem.item?.inventory?.unit || 'N/A'
                }
              })) || []
            };
            processedPOs.unshift(processedCurrentPO); // Add to beginning
          } else if (!poExists) {
            // If we only have the ID, fetch it
            try {
              const currentPOResponse = await inventoryApi.getPurchaseOrderById(currentPOId);
              const currentPO = currentPOResponse.data?.data || currentPOResponse.data;
              if (currentPO) {
                // Process the current PO similar to others
                const processedCurrentPO = {
                  ...currentPO,
                  lineItems: currentPO.lineItems?.map(lineItem => ({
                    ...lineItem,
                    item: {
                      ...lineItem.item,
                      _id: lineItem.item?._id || lineItem.item,
                      name: lineItem.item?.name || 'Unknown Item',
                      code: lineItem.item?.code || '',
                      sku: lineItem.item?.sku || '',
                      unit: lineItem.item?.unit || lineItem.item?.inventory?.unit || 'N/A'
                    }
                  })) || []
                };
                processedPOs.unshift(processedCurrentPO); // Add to beginning
              }
            } catch (currentPOError) {
              console.warn('Could not fetch current GRN PO:', currentPOError);
            }
          }
        }

        setPendingPOs(processedPOs);
      } catch (apiErr) {
        console.warn('Could not load pending POs (endpoint may not exist):', apiErr.message);
        // Fallback: try to get all purchase orders and filter by supplier
        try {
          const allPOsResponse = await inventoryApi.getPurchaseOrders({
            supplier: supplierId,
            status: 'approved',
            populate: 'lineItems.item' // Request population
          });
          const allPOs = allPOsResponse.data?.data || allPOsResponse.data || [];
          let pendingPOs = allPOs.filter(po => po.status === 'approved' || po.status === 'partially_received');

          // In edit mode, also include the current GRN's PO if it exists
          if (isEditMode && formData.purchaseOrder) {
            const currentPOId = formData.purchaseOrder._id || formData.purchaseOrder;
            const currentPO = allPOs.find(po => po._id === currentPOId);
            if (currentPO && !pendingPOs.some(po => po._id === currentPOId)) {
              pendingPOs.unshift(currentPO);
            }
          }

          // Process the fallback data similarly
          const processedPOs = pendingPOs.map(po => ({
            ...po,
            lineItems: po.lineItems?.map(lineItem => ({
              ...lineItem,
              item: {
                ...lineItem.item,
                _id: lineItem.item?._id || lineItem.item,
                name: lineItem.item?.name || 'Unknown Item',
                code: lineItem.item?.code || '',
                sku: lineItem.item?.sku || '',
                unit: lineItem.item?.unit || lineItem.item?.inventory?.unit || 'N/A'
              }
            })) || []
          }));

          setPendingPOs(processedPOs);
        } catch (fallbackErr) {
          console.error('Fallback PO loading also failed:', fallbackErr);
          setPendingPOs([]);
          toast.error(fallbackErr?.response?.data?.message || 'Could not load purchase orders. The feature may not be fully implemented yet.');
        }
      }
    } catch (err) {
      console.error('Error loading pending POs:', err);
      toast.error(err?.response?.data?.message || 'Failed to load pending purchase orders.');
      setPendingPOs([]);
    } finally {
      setLoadingPOs(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSupplierChange = (selectedSupplier) => {
    setFormData(prev => ({
      ...prev,
      supplier: selectedSupplier?._id || null,
      selectedSupplier: selectedSupplier,
      purchaseOrder: null, // Clear PO when supplier changes
      lineItems: [] // Clear items when supplier changes
    }));

    if (selectedSupplier && formData.withPO) {
      loadPendingPOs(selectedSupplier._id);
    } else {
      setPendingPOs([]);
    }
  };

  const handlePOChange = (selectedPO) => {
    if (!selectedPO) {
      setFormData(prev => ({
        ...prev,
        purchaseOrder: null,
        lineItems: [],
        costCenter: '',
        costCenterLabel: ''
      }));
      return;
    }


    // Map PO line items to GRN line items
    const grnLineItems = selectedPO.lineItems?.map((poItem, index) => {

      // Get item details from populated data or find in items array
      let itemDetails = poItem.item;

      // If poItem.item is just an ID, try to find the full item details
      if (typeof poItem.item === 'string' || (poItem.item && !poItem.item.name)) {
        const itemId = typeof poItem.item === 'string' ? poItem.item : poItem.item._id;
        itemDetails = items.find(item => item._id === itemId) || poItem.item;
      }

      // Calculate pending quantity: ordered - already received
      const orderedQty = poItem.quantity || 0;
      const alreadyReceivedQty = poItem.receivedQuantity || 0;
      const pendingQty = poItem.pendingQuantity !== undefined 
        ? poItem.pendingQuantity 
        : Math.max(0, orderedQty - alreadyReceivedQty);

      const grnItem = {
        purchaseOrderItemId: poItem._id,
        item: itemDetails, // Use the full item object
        assetId: poItem.assetId?._id || poItem.assetId || null,
        serviceId: poItem.serviceId?._id || poItem.serviceId || null,
        assetCode: poItem.assetCode || '',
        assetName: poItem.assetName || '',
        description: poItem.description || itemDetails?.name || itemDetails?.itemName || poItem.assetName || 'Unknown Item',
        orderedQuantity: orderedQty,
        receivedQuantity: alreadyReceivedQty, // What was already received before this GRN
        deliveredQuantity: pendingQty, // What we're receiving now (pending quantity)
        acceptedQuantity: 0,
        rejectedQuantity: 0,
        unitPrice: poItem.unitPrice || 0,
        condition: 'good',
        batchNumber: '',
        expiryDate: '',
        notes: ''
      };

      return grnItem;
    }) || [];


    // Automatically select store from PO
    let storeToSelect = formData.store;
    const poStore = selectedPO.warehouse || selectedPO.location || selectedPO.deliveryLocation;
    const storeId = poStore?._id ? String(poStore._id) : (poStore ? String(poStore) : null);
    if (storeId) {
      const storeExists = stores.find(store => String(store._id) === storeId);
      if (storeExists) storeToSelect = storeId;
    }

    setFormData(prev => ({
      ...prev,
      purchaseOrder: selectedPO,
      store: storeToSelect,
      procurementType: selectedPO.procurementType || prev.procurementType,
      ...(selectedPO.procurementType === 'SERVICE' && !prev.deliveryNote.date && selectedPO.expectedDeliveryDate && {
        deliveryNote: { ...prev.deliveryNote, date: new Date(selectedPO.expectedDeliveryDate).toISOString().split('T')[0] }
      }),
      lineItems: grnLineItems,
      costCenter: selectedPO.costCenter || prev.costCenter,
      costCenterLabel: selectedPO.costCenterLabel || prev.costCenterLabel
    }));

    // Carry over enabled state and rates from the PO; fall back to tax config defaults
    setSsclEnabled(selectedPO.totals?.ssclAmount > 0);
    setSsclRate(selectedPO.totals?.ssclRate > 0 ? String(selectedPO.totals.ssclRate) : taxDefaults.ssclRate);
    setVatEnabled(selectedPO.totals?.vatAmount > 0);
    setVatRate(selectedPO.totals?.vatRate > 0 ? String(selectedPO.totals.vatRate) : taxDefaults.vatRate);
  };

  const togglePOMode = (withPO) => {
    setFormData(prev => ({
      ...prev,
      withPO,
      purchaseOrder: null,
      selectedSupplier: null,
      supplier: null,
      lineItems: [],
      costCenter: '',
      costCenterLabel: ''
    }));
    setPendingPOs([]);
  };

  const handleProcurementTypeChange = (value) => {
    // Switching type invalidates existing line refs — reset the items list.
    // A service completion is always raised from its service PO, so Service switches to "With Purchase Order".
    setFormData(prev => ({
      ...prev,
      procurementType: value,
      lineItems: [],
      ...(value === 'SERVICE' && !prev.withPO && { withPO: true, purchaseOrder: null, selectedSupplier: null, supplier: null })
    }));
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      lineItems: [...prev.lineItems, {
        purchaseOrderItemId: null,
        item: null,
        assetId: null,
        assetCode: '',
        assetName: '',
        description: '',
        orderedQuantity: 0,
        receivedQuantity: 0,
        deliveredQuantity: 0,
        acceptedQuantity: 0,
        rejectedQuantity: 0,
        unitPrice: 0,
        condition: 'good',
        batchNumber: '',
        expiryDate: '',
        notes: ''
      }]
    }));
  };

  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      lineItems: prev.lineItems.filter((_, i) => i !== index)
    }));
  };

  const handleItemChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      lineItems: prev.lineItems.map((item, i) => {
        if (i !== index) return item;

        let updatedItem = { ...item, [field]: value };

        // Auto-calculate delivered quantity when received quantity changes
        if (field === 'receivedQuantity') {
          updatedItem.deliveredQuantity = value;
          // Auto-set accepted quantity to received quantity initially
          if (updatedItem.acceptedQuantity === 0 && updatedItem.rejectedQuantity === 0) {
            updatedItem.acceptedQuantity = value;
          }
        }

        // When item is selected, populate description and other details
        if (field === 'item' && value) {
          const itemPool = formData.withPO ? items : filteredItems;
          const selectedItem = itemPool.find(item => item._id === (value._id || value));
          if (selectedItem) {
            updatedItem.item = selectedItem;
            updatedItem.description = selectedItem.name || selectedItem.itemName || '';
            // Manual GRN: start from the item's purchase cost (editable); a GRN from a PO keeps the PO price
            if (!formData.withPO) {
              const price = Number(selectedItem.pricing?.unitCost) || Number(selectedItem.unitCost)
                || Number(selectedItem.pricing?.sellingPrice) || Number(selectedItem.unitPrice) || 0;
              updatedItem.unitPrice = price;
            }
          }
        }

        // When a fixed asset is selected, populate code/name/description and price
        if (field === 'assetId' && value) {
          const selectedAsset = assets.find(a => a._id === (value._id || value));
          if (selectedAsset) {
            updatedItem.assetId = selectedAsset._id;
            updatedItem.assetCode = selectedAsset.code || '';
            updatedItem.assetName = selectedAsset.name || '';
            updatedItem.description = selectedAsset.name || '';
            if (!formData.withPO) {
              updatedItem.unitPrice = selectedAsset.purchasePrice || selectedAsset.marketPrice || updatedItem.unitPrice || 0;
            }
          }
        }

        // Ensure accepted + rejected doesn't exceed delivered
        if (field === 'acceptedQuantity' || field === 'rejectedQuantity') {
          const otherField = field === 'acceptedQuantity' ? 'rejectedQuantity' : 'acceptedQuantity';
          const otherValue = updatedItem[otherField];
          const totalProcessed = value + otherValue;

          if (totalProcessed > updatedItem.deliveredQuantity) {
            // Adjust the other field to not exceed delivered quantity
            updatedItem[otherField] = Math.max(0, updatedItem.deliveredQuantity - value);
          }
        }

        return updatedItem;
      })
    }));
  };

  const validateForm = () => {
    const errors = [];

    if (!formData.receiptDate) errors.push('Receipt date is required');
    if (!formData.supplier) errors.push('Supplier is required');
    if (!formData.costCenter) errors.push('Cost center is required');

    // Enhanced store validation with better messaging (a service completion has no store)
    if (formData.procurementType === 'SERVICE') {
      // nothing to validate
    } else if (!formData.store) {
      errors.push('Store/Location is required');
    } else if (!stores.find(s => String(s._id) === String(formData.store))) {
      errors.push('Selected store/location is invalid. Please select a valid store from the dropdown.');
    }

    if (formData.withPO && !formData.purchaseOrder) errors.push('Purchase order is required when creating GRN with PO');
    if (formData.lineItems.length === 0) errors.push('At least one item is required');

    const isFixedAsset = formData.procurementType === 'FIXED_ASSET';
    const isService = formData.procurementType === 'SERVICE';
    if (isService && !formData.withPO) errors.push('A service completion is raised from a service purchase order');
    formData.lineItems.forEach((item, index) => {
      if (isService) {
        if (!item.serviceId) errors.push(`Line ${index + 1}: service is missing — load the lines from the service purchase order`);
      } else if (isFixedAsset) {
        if (!item.assetId) errors.push(`Item ${index + 1}: Asset selection is required`);
      } else if (!item.item) {
        errors.push(`Item ${index + 1}: Item selection is required`);
      }
      if (item.deliveredQuantity <= 0) errors.push(`Item ${index + 1}: Delivered quantity must be greater than 0`);
      if (item.unitPrice < 0) errors.push(`Item ${index + 1}: Unit price cannot be negative`);
      if ((item.unitPrice || 0) === 0) errors.push(`Item ${index + 1}: Unit price must be greater than 0`);
      if (item.acceptedQuantity + item.rejectedQuantity > item.deliveredQuantity) {
        errors.push(`Item ${index + 1}: Accepted + Rejected quantities cannot exceed delivered quantity`);
      }
      // Validate that total value can be calculated
      const totalValue = (item.deliveredQuantity || 0) * (item.unitPrice || 0);
      if (totalValue <= 0) {
        errors.push(`Item ${index + 1}: Total value must be greater than 0 (check quantity and unit price)`);
      }
    });

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      toast.error(validationErrors.join('. '));
      return;
    }

    try {
      setLoading(true);

      const isFixedAsset = formData.procurementType === 'FIXED_ASSET';
      const payload = {
        purchaseOrderId: formData.withPO ? formData.purchaseOrder?._id : null,
        supplierId: formData.supplier,
        storeId: formData.procurementType === 'SERVICE' ? null : formData.store,
        procurementType: formData.procurementType,
        receiptDate: formData.receiptDate,
        deliveryNote: {
          number: formData.deliveryNote.number || '',
          date: formData.deliveryNote.date ? new Date(formData.deliveryNote.date).toISOString() : null
        },
        invoice: {
          number: formData.invoice.number || '',
          date: formData.invoice.date ? new Date(formData.invoice.date).toISOString() : null,
          amount: formData.invoice.amount || 0
        },
        lineItems: formData.lineItems.map(item => ({
          purchaseOrderItemId: item.purchaseOrderItemId || null,
          ...(isFixedAsset
            ? {
                assetId: item.assetId?._id || item.assetId, assetCode: item.assetCode || '', assetName: item.assetName || '',
                // One serial per accepted unit — each unit becomes an Asset Register record
                serialNumbers: String(item.serialNumbersText || '').split(/[\n,]/).map(s => s.trim()).filter(Boolean)
              }
            : formData.procurementType === 'SERVICE'
              ? { serviceId: item.serviceId?._id || item.serviceId }
              : { item: item.item?._id || item.item }),
          description: item.description,
          orderedQuantity: item.orderedQuantity || 0,
          deliveredQuantity: item.deliveredQuantity,
          acceptedQuantity: item.acceptedQuantity,
          rejectedQuantity: item.rejectedQuantity,
          unitPrice: item.unitPrice,
          totalValue: (item.deliveredQuantity || 0) * (item.unitPrice || 0), // Add totalValue calculation
          condition: item.condition || 'good',
          batchNumber: item.batchNumber || '',
          expiryDate: item.expiryDate ? new Date(item.expiryDate).toISOString() : null,
          manufacturingDate: item.manufacturingDate ? new Date(item.manufacturingDate).toISOString() : null,
          notes: item.notes || ''
        })),
        qualityInspection: {
          required: false
        },
        notes: formData.notes || '',
        costCenter: formData.costCenter,
        ssclAmount: parseFloat(ssclAmount.toFixed(2)),
        ssclRate: ssclEnabled ? parseFloat(ssclRate) || 0 : 0,
        vatAmount: parseFloat(vatAmount.toFixed(2)),
        vatRate: vatEnabled ? parseFloat(vatRate) || 0 : 0
      };

      // Server errors (budget, PO quantities, validation…) are shown as they come back
      if (isEditMode) {
        await inventoryApi.updateGRN(grn._id, payload);
      } else {
        const res = await inventoryApi.createGRN(payload);
        await docsRef.current?.uploadPending(res?.data?._id);
      }

      onSuccess();
    } catch (err) {
      console.error('Error saving GRN:', err);
      let errorMessage = 'Failed to save GRN. Please try again.';

      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }

      // Handle validation errors specifically
      if (errorMessage.includes('Validation failed')) {
        // Extract validation details if available
        if (err.response?.data?.details) {
          const validationDetails = err.response.data.details;
          if (Array.isArray(validationDetails)) {
            errorMessage = `Validation failed: ${validationDetails.join(', ')}`;
          }
        }
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 backdrop-blur-sm bg-black/40 overflow-y-auto z-50 flex items-center justify-center p-4">
      <div className="relative w-full max-w-6xl max-h-[90vh] bg-white shadow-2xl border-0 flex flex-col rounded-2xl overflow-hidden">
        {/* Header */}
        <div className="flex-shrink-0 bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-3 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white">
                  {formData.procurementType === 'SERVICE' ? (isEditMode ? 'Edit Service Completion' : 'New Service Completion') : (isEditMode ? 'Edit Goods Received Note' : 'Create New GRN')}
                </h3>
                <p className="text-indigo-100 text-sm mt-1">
                  {isEditMode ? `Modifying ${formData.procurementType === 'SERVICE' ? 'service completion' : 'GRN'} ${grn?.grnNumber || ''}` : formData.procurementType === 'SERVICE' ? 'Confirm the service work completed by the supplier' : 'Record incoming inventory items'}
                </p>
              </div>
            </div>
            <button
              onClick={onCancel}
              className="w-10 h-10 rounded-xl flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-all duration-200"
              disabled={loading}
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        {loadingStores && (
          <div className="flex-1 flex flex-col items-center justify-center py-16 px-6">
            <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mb-4" />
            <p className="text-sm font-medium text-slate-600">Loading form data...</p>
            <p className="text-xs text-slate-400 mt-1">Fetching suppliers, items and store locations</p>
          </div>
        )}

        <div className={`flex-1 overflow-y-auto p-3 ${loadingStores ? 'hidden' : ''}`}>

          {error && (
            <div className="mb-3 bg-gradient-to-r from-red-50 via-orange-50 to-red-50 border border-red-200 rounded-lg p-3 shadow-sm">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-4 w-4 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-2">
                  <p className="text-xs font-medium text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-3">
            {/* GRN Creation Mode Selection - App Theme */}
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-3 rounded-lg border border-indigo-200 shadow-sm">
              <h3 className="text-xs font-bold text-indigo-800 mb-3 flex items-center">
                <svg className="w-4 h-4 mr-2 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Receipt Creation Mode
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className={`flex items-center p-3 bg-white rounded-lg border-2 transition-all cursor-pointer hover:shadow-md ${formData.withPO ? 'border-indigo-300 bg-indigo-50/50' : 'border-gray-200 hover:border-indigo-200'}`}>
                  <input
                    id="withPO"
                    type="radio"
                    name="grnMode"
                    checked={formData.withPO}
                    onChange={() => togglePOMode(true)}
                    className="h-3 w-3 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                  />
                  <label htmlFor="withPO" className="ml-2 cursor-pointer flex-1">
                    <div className="flex items-center">
                      <svg className="w-3 h-3 mr-1 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span className="font-semibold text-gray-900 text-sm">With Purchase Order</span>
                    </div>
                    <p className="text-xs text-gray-600 mt-0.5">Receive items against existing PO</p>
                  </label>
                </div>
                <div className={`flex items-center p-3 bg-white rounded-lg border-2 transition-all ${formData.procurementType === 'SERVICE' ? 'opacity-60 cursor-not-allowed border-gray-200' : 'cursor-pointer hover:shadow-md'} ${!formData.withPO ? 'border-indigo-300 bg-indigo-50/50' : 'border-gray-200 hover:border-indigo-200'}`}>
                  <input
                    id="manualEntry"
                    type="radio"
                    name="grnMode"
                    checked={!formData.withPO}
                    disabled={formData.procurementType === 'SERVICE'}
                    onChange={() => togglePOMode(false)}
                    className="h-3 w-3 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                  />
                  <label htmlFor="manualEntry" className="ml-2 cursor-pointer flex-1">
                    <div className="flex items-center">
                      <svg className="w-3 h-3 mr-1 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      <span className="font-semibold text-gray-900 text-sm">Manual Entry</span>
                    </div>
                    <p className="text-xs text-gray-600 mt-0.5">{formData.procurementType === 'SERVICE' ? 'Not for services — a service completion comes from its service PO' : 'Add items manually without PO'}</p>
                  </label>
                </div>
              </div>
            </div>

            {/* Receipt Basic Information */}
            <div className="bg-gradient-to-r from-slate-50 via-gray-50 to-zinc-50 p-3 rounded-lg border border-slate-200 shadow-sm">
              <h3 className="text-xs font-bold text-slate-800 mb-3 flex items-center">
                <svg className="w-4 h-4 mr-2 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Receipt Information
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Procurement Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.procurementType}
                    onChange={(e) => handleProcurementTypeChange(e.target.value)}
                    disabled={formData.withPO && !!formData.purchaseOrder}
                    className={`block w-full rounded-lg border-0 py-2 px-3 text-xs text-gray-900 shadow-sm ring-1 ring-inset ring-slate-200 focus:ring-2 focus:ring-indigo-500 transition-all ${formData.withPO && formData.purchaseOrder ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : 'bg-white hover:ring-slate-300'}`}
                  >
                    <option value="INVENTORY">Inventory</option>
                    <option value="FIXED_ASSET">Fixed Asset</option>
                    <option value="SERVICE">Service</option>
                  </select>
                  {formData.withPO && formData.purchaseOrder && (
                    <p className="mt-1 text-xs text-indigo-600">Inherited from purchase order</p>
                  )}
                </div>
                <div>
                  <label htmlFor="receiptDate" className="block text-xs font-bold text-slate-700 mb-1">
                    {formData.procurementType === 'SERVICE' ? 'Completion Date *' : 'Receipt Date *'}
                  </label>
                  <input
                    id="receiptDate"
                    type="date"
                    name="receiptDate"
                    value={formData.receiptDate}
                    onChange={handleChange}
                    className="block w-full rounded-lg border-0 bg-white py-2 px-3 text-xs text-gray-900 shadow-sm ring-1 ring-inset ring-slate-200 hover:ring-slate-300 focus:ring-2 focus:ring-indigo-500 transition-all"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="supplier" className="block text-xs font-bold text-slate-700 mb-1">
                    Supplier * {formData.withPO && <span className="text-indigo-600 font-medium">(with POs)</span>}
                  </label>
                  <SearchableSelect
                    value={formData.selectedSupplier?._id || ''}
                    onChange={(e) => {
                      const list = formData.withPO ? suppliersWithPOs : suppliers;
                      const selectedSupplier = list.find(s => s._id === e.target.value);
                      handleSupplierChange(selectedSupplier || null);
                    }}
                    options={(formData.withPO ? suppliersWithPOs : suppliers).map(s => ({
                      value: s._id,
                      label: `${s.name || s.supplierName || 'Unknown'}${s.code ? ` (${s.code})` : ''}`
                    }))}
                    placeholder="Select a supplier"
                    size="sm"
                  />
                  {formData.withPO && formData.selectedSupplier && (
                    <p className="mt-1 text-xs text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded-md">
                      {(() => {
                        const supplier = suppliersWithPOs.find(s => s._id === formData.selectedSupplier._id);
                        const poCount = supplier?.pendingPOCount || supplier?.pendingPOs?.length || 0;
                        return `📋 ${poCount} pending purchase order(s) available`;
                      })()}
                    </p>
                  )}
                </div>

                {formData.withPO && (
                  <div>
                    <label htmlFor="purchaseOrder" className="block text-xs font-bold text-slate-700 mb-1">
                      Purchase Order *
                    </label>
                    <SearchableSelect
                      value={formData.purchaseOrder?._id || ''}
                      onChange={(e) => {
                        const selectedPO = pendingPOs.find(po => po._id === e.target.value);
                        handlePOChange(selectedPO || null);
                      }}
                      options={pendingPOs.map(po => {
                        const orderNumber = po.orderNumber || po.poNumber || `PO-${po._id?.slice(-6)?.toUpperCase()}`;
                        const orderDate = po.orderDate ? new Date(po.orderDate).toLocaleDateString() : 'N/A';
                        const itemCount = po.lineItems?.length || 0;
                        return { value: po._id, label: `${orderNumber} (${orderDate}) — ${itemCount} items` };
                      })}
                      placeholder={loadingPOs ? 'Loading purchase orders...' : 'Select a purchase order'}
                      disabled={!formData.selectedSupplier || loadingPOs}
                      size="sm"
                    />
                    {loadingPOs && (
                      <p className="mt-1 text-xs text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-md">
                        🔄 Loading available purchase orders...
                      </p>
                    )}
                  </div>
                )}
                {formData.procurementType === 'SERVICE' ? (
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Service Location</label>
                  <div className="px-2 py-1.5 text-sm bg-slate-50 border border-slate-200 rounded text-slate-700">{currentLocation?.name || 'Current location'}</div>
                  <p className="mt-1 text-[10px] text-slate-400">No store — the service was done at this location</p>
                </div>
                ) : (
                <div>
                  <label htmlFor="store" className="block text-xs font-bold text-slate-700 mb-1">
                    Store/Location * {loadingStores && <span className="text-indigo-600">(Loading...)</span>}
                  </label>
                  <SearchableSelect
                    value={formData.store || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, store: e.target.value || null }))}
                    options={stores.map(s => ({
                      value: s._id,
                      label: `${s.name || s.locationName || 'Unknown'}${s.code ? ` (${s.code})` : ''}`
                    }))}
                    placeholder={loadingStores ? 'Loading stores...' : 'Select a store/location'}
                    disabled={loadingStores}
                    error={formData.store && !loadingStores && !stores.find(s => String(s._id) === String(formData.store)) ? 'Selected store not found' : ''}
                    size="sm"
                  />
                  {loadingStores && (
                    <p className="mt-1 text-xs text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-md">
                      🔄 Loading available stores/locations...
                    </p>
                  )}
                  {formData.store && !loadingStores && !stores.find(s => String(s._id) === String(formData.store)) && (
                    <p className="mt-1 text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded-md">
                      ⚠️ Selected store/location not found. Please select a valid location.
                    </p>
                  )}
                  {isEditMode && formData.store && !loadingStores && stores.find(s => String(s._id) === String(formData.store)) && (
                    <p className="mt-1 text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded-md">
                      ✓ Store/location loaded from existing GRN
                    </p>
                  )}
                  {!loadingStores && stores.length === 0 && (
                    <p className="mt-1 text-xs text-yellow-600 bg-yellow-50 px-2 py-0.5 rounded-md">
                      ⚠️ No stores/locations available. Please configure warehouses in the system.
                    </p>
                  )}
                </div>
                )}
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Cost Center <span className="text-red-500">*</span>
                  </label>
                  <SearchableCostCenterDropdown
                    value={formData.costCenter}
                    displayLabel={formData.costCenterLabel}
                    onChange={(id, label) => {
                      setFormData(prev => ({ ...prev, costCenter: id || '', costCenterLabel: label || '' }));
                    }}
                    placeholder="Select cost center"
                    disabled={formData.withPO && !!formData.purchaseOrder}
                    limit={50}
                    menuPlacement="down"
                  />
                  {formData.withPO && formData.purchaseOrder && (
                    <p className="mt-1 text-xs text-indigo-600">Inherited from purchase order</p>
                  )}
                </div>
                <div>
                  <label htmlFor="referenceNumber" className="block text-xs font-bold text-slate-700 mb-1">
                    Reference Number
                  </label>
                  <input
                    id="referenceNumber"
                    type="text"
                    name="referenceNumber"
                    value={formData.referenceNumber}
                    onChange={(e) => setFormData(prev => ({ ...prev, referenceNumber: e.target.value }))}
                    className="block w-full rounded-lg border-0 bg-gray-50 py-2 px-3 text-xs text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 hover:ring-gray-300 focus:ring-2 focus:ring-indigo-500 hover:bg-white transition-all"
                    placeholder="Optional reference number"
                  />
                </div>
              </div>
            </div>

            {/* Compact Document Information */}
            <div className="bg-gradient-to-r from-gray-50 to-blue-50 p-3 rounded-lg border border-gray-200">
              <h3 className="text-xs font-semibold text-gray-800 mb-2 flex items-center">
                <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Document Information
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
                <div>
                  <label htmlFor="deliveryNoteNumber" className="block text-xs font-semibold text-gray-800 mb-0.5">
                    {formData.procurementType === 'SERVICE' ? 'Service Report / Job Sheet #' : 'Delivery Note #'}
                  </label>
                  <input
                    id="deliveryNoteNumber"
                    type="text"
                    value={formData.deliveryNote.number}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      deliveryNote: { ...prev.deliveryNote, number: e.target.value }
                    }))}
                    className="block w-full rounded-lg border-0 bg-gray-50 py-1.5 px-2 text-xs text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 hover:ring-gray-300 focus:ring-2 focus:ring-indigo-500 hover:bg-white transition-all"
                    placeholder={formData.procurementType === 'SERVICE' ? 'Service report / job sheet number' : 'Delivery note number'}
                  />
                </div>

                <div>
                  <label htmlFor="deliveryDate" className="block text-xs font-semibold text-gray-800 mb-0.5">
                    {formData.procurementType === 'SERVICE' ? 'Service Date' : 'Delivery Date'}
                  </label>
                  <input
                    id="deliveryDate"
                    type="date"
                    value={formData.deliveryNote.date}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      deliveryNote: { ...prev.deliveryNote, date: e.target.value }
                    }))}
                    className="block w-full rounded-lg border-0 bg-white py-1.5 px-2 text-xs text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 hover:ring-gray-300 focus:ring-2 focus:ring-indigo-500 transition-all"
                  />
                </div>

                <div>
                  <label htmlFor="invoiceNumber" className="block text-xs font-semibold text-gray-800 mb-0.5">
                    Invoice #
                  </label>
                  <input
                    id="invoiceNumber"
                    type="text"
                    value={formData.invoice.number}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      invoice: { ...prev.invoice, number: e.target.value }
                    }))}
                    className="block w-full rounded-lg border-0 bg-gray-50 py-1.5 px-2 text-xs text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 hover:ring-gray-300 focus:ring-2 focus:ring-indigo-500 hover:bg-white transition-all"
                    placeholder="Invoice number"
                  />
                </div>

                <div>
                  <label htmlFor="invoiceAmount" className="block text-xs font-semibold text-gray-800 mb-0.5">
                    Invoice Amount
                  </label>
                  <input
                    id="invoiceAmount"
                    type="number"
                    step="0.01"
                    value={formData.invoice.amount}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      invoice: { ...prev.invoice, amount: parseFloat(e.target.value) || 0 }
                    }))}
                    className="block w-full rounded-lg border-0 bg-gray-50 py-1.5 px-2 text-xs text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 hover:ring-gray-300 focus:ring-2 focus:ring-indigo-500 hover:bg-white transition-all"
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>

            {/* Compact Items Section */}
            <div className="bg-gradient-to-r from-gray-50 to-blue-50 p-3 rounded-lg border border-gray-200">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-xs font-semibold text-gray-800 flex items-center">
                  <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  Line Items {formData.lineItems.length > 0 && (
                    <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                      {formData.lineItems.length}
                    </span>
                  )}
                </h3>
                {!formData.withPO && (
                  <button
                    type="button"
                    onClick={addItem}
                    className="px-2 py-1 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-xs font-medium rounded-md transition-all duration-200 transform hover:scale-105 flex items-center"
                  >
                    <PlusIcon className="h-3 w-3 mr-1" />
                    Add Item
                  </button>
                )}
              </div>

              {formData.lineItems.length === 0 ? (
                <div className="text-center py-6 bg-white rounded-lg border-2 border-dashed border-gray-300">
                  <svg className="mx-auto h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  <p className="mt-1 text-xs text-gray-500">
                    {formData.withPO
                      ? 'Select a supplier and purchase order to load items'
                      : !formData.supplier
                      ? 'Select a supplier to load available items'
                      : 'No items added yet'}
                  </p>
                  {!formData.withPO && formData.supplier && (
                    <p className="text-xs text-gray-400 mt-0.5">Click "Add Item" to start adding items manually</p>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {formData.lineItems.map((item, index) => (
                    <div key={index} className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                      <div className="grid grid-cols-1 lg:grid-cols-7 gap-3">
                        <div className="lg:col-span-2">
                          <label className="block text-xs font-semibold text-gray-800 mb-0.5">
                            {formData.procurementType === 'FIXED_ASSET' ? 'Fixed Asset *' : formData.procurementType === 'SERVICE' ? 'Service *' : 'Item *'}
                          </label>
                          {formData.withPO ? (
                            <div className="p-2 bg-gradient-to-r from-gray-50 to-blue-50 rounded-lg border">
                              <div className="font-semibold text-gray-900 text-xs">
                                {(() => {
                                  // Try multiple ways to get the item/asset name
                                  const itemName = item.assetName ||
                                    item.item?.name ||
                                    item.item?.itemName ||
                                    item.description ||
                                    'Unknown';
                                  return itemName;
                                })()}
                              </div>
                              {(item.assetCode || item.item?.code || item.item?.sku) && (
                                <div className="text-xs text-gray-600 mt-0.5">
                                  Code: {item.assetCode || item.item?.code || item.item?.sku}
                                </div>
                              )}
                            </div>
                          ) : formData.procurementType === 'FIXED_ASSET' ? (
                            <SearchableSelect
                              value={item.assetId?._id || item.assetId || ''}
                              onChange={(e) => {
                                handleItemChange(index, 'assetId', e.target.value || null);
                              }}
                              options={assets.map(a => ({
                                value: a._id,
                                label: `${a.name || 'Unknown'}${a.code ? ` (${a.code})` : ''}${a.serialNumber ? ` — ${a.serialNumber}` : ''}`
                              }))}
                              placeholder={assets.length === 0 ? 'No fixed assets available' : 'Select a fixed asset'}
                              size="sm"
                            />
                          ) : (
                            <SearchableSelect
                              value={item.item?._id || item.item || ''}
                              onChange={(e) => {
                                const selectedItem = filteredItems.find(itm => itm._id === e.target.value);
                                handleItemChange(index, 'item', selectedItem || null);
                              }}
                              options={filteredItems.map(itm => ({
                                value: itm._id,
                                label: `${itm.name || itm.itemName || 'Unknown'}${itm.code ? ` (${itm.code})` : ''}`
                              }))}
                              placeholder={
                                !formData.supplier
                                  ? 'Select a supplier first'
                                  : items.length === 0
                                  ? 'No items available'
                                  : 'Select an item'
                              }
                              disabled={!formData.supplier}
                              size="sm"
                            />
                          )}
                        </div>

                        <div>
                          <label className="block text-xs font-semibold text-gray-800 mb-0.5">
                            UOM
                          </label>
                          <div className="p-1.5 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg border border-amber-200 font-semibold text-amber-800 text-center text-xs">
                            {(() => {
                              // Get UOM from multiple sources with priority order
                              if (formData.withPO && formData.purchaseOrder) {
                                // For PO mode: try to get unit from the corresponding PO line item
                                const poLineItem = formData.purchaseOrder.lineItems?.find(
                                  poItem => poItem._id === item.purchaseOrderItemId
                                );
                                if (poLineItem?.unit) return poLineItem.unit;
                              }

                              // Fall back to item's inventory unit or general unit
                              return item.item?.inventory?.unit ||
                                item.item?.unit ||
                                item.item?.unitOfMeasure ||
                                'N/A';
                            })()}
                          </div>
                        </div>

                        {formData.withPO && (
                          <div>
                            <label className="block text-xs font-semibold text-gray-800 mb-0.5">
                              Ordered Qty
                            </label>
                            <div className="p-1.5 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200 font-semibold text-blue-800 text-xs">
                              {item.orderedQuantity}
                            </div>
                          </div>
                        )}

                        <div>
                          <label className="block text-xs font-semibold text-gray-800 mb-0.5">
                            {formData.procurementType === 'SERVICE' ? 'Completed Qty *' : 'Delivered Qty *'}
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={item.deliveredQuantity}
                            onChange={(e) => handleItemChange(index, 'deliveredQuantity', parseFloat(e.target.value) || 0)}
                            className="block w-full rounded-lg border-0 bg-white py-1.5 px-2 text-xs text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 hover:ring-gray-300 focus:ring-2 focus:ring-indigo-500 transition-all"
                            required
                            min="0"
                            max={formData.withPO ? item.orderedQuantity : undefined}
                            placeholder="0"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-semibold text-gray-800 mb-0.5">
                            Unit Price *
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={item.unitPrice}
                            onChange={(e) => handleItemChange(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                            className="block w-full rounded-lg border-0 bg-white py-1.5 px-2 text-xs text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 hover:ring-gray-300 focus:ring-2 focus:ring-indigo-500 transition-all"
                            required
                            min="0"
                            placeholder="0.00"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-semibold text-gray-800 mb-0.5">
                            Total Value
                          </label>
                          <div className="p-1.5 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200 font-bold text-green-800 text-xs">
                            {(item.deliveredQuantity * item.unitPrice).toFixed(2)}
                          </div>
                        </div>

                        {!formData.withPO && (
                          <div className="flex items-end">
                            <button
                              type="button"
                              onClick={() => removeItem(index)}
                              className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                              title="Remove Item"
                            >
                              <TrashIcon className="h-3 w-3" />
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Compact Quality Control & Details */}
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <h4 className="text-xs font-semibold text-gray-800 mb-2 flex items-center">
                          <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Quality Control & Details
                        </h4>

                        {/* Quality quantities */}
                        <div className="grid grid-cols-1 lg:grid-cols-4 gap-2 mb-3">
                          <div>
                            <label className="block text-xs font-semibold text-gray-800 mb-0.5">
                              Accepted Qty
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              value={item.acceptedQuantity}
                              onChange={(e) => handleItemChange(index, 'acceptedQuantity', parseFloat(e.target.value) || 0)}
                              className="block w-full rounded-lg border-0 bg-green-50 py-1.5 px-2 text-xs text-gray-900 shadow-sm ring-1 ring-inset ring-green-200 hover:ring-green-300 focus:ring-2 focus:ring-green-500 transition-all"
                              min="0"
                              max={item.deliveredQuantity}
                              placeholder="0"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-semibold text-gray-800 mb-0.5">
                              Rejected Qty
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              value={item.rejectedQuantity}
                              onChange={(e) => handleItemChange(index, 'rejectedQuantity', parseFloat(e.target.value) || 0)}
                              className="block w-full rounded-lg border-0 bg-red-50 py-1.5 px-2 text-xs text-gray-900 shadow-sm ring-1 ring-inset ring-red-200 hover:ring-red-300 focus:ring-2 focus:ring-red-500 transition-all"
                              min="0"
                              max={item.deliveredQuantity - item.acceptedQuantity}
                              placeholder="0"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-semibold text-gray-800 mb-0.5">
                              Condition
                            </label>
                            <select
                              value={item.condition || 'good'}
                              onChange={(e) => handleItemChange(index, 'condition', e.target.value)}
                              className="block w-full rounded-lg border-0 bg-white py-1.5 px-2 text-xs text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 hover:ring-gray-300 focus:ring-2 focus:ring-indigo-500 transition-all"
                            >
                              <option value="good">Good</option>
                              <option value="damaged">Damaged</option>
                              <option value="defective">Defective</option>
                              <option value="expired">Expired</option>
                              <option value="short_shipped">Short Shipped</option>
                            </select>
                          </div>

                          <div className={formData.procurementType === 'SERVICE' ? 'hidden' : ''}>
                            <label className="block text-xs font-semibold text-gray-800 mb-0.5">
                              Batch Number
                            </label>
                            <input
                              type="text"
                              value={item.batchNumber}
                              onChange={(e) => handleItemChange(index, 'batchNumber', e.target.value)}
                              className="block w-full rounded-lg border-0 bg-gray-50 py-1.5 px-2 text-xs text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 hover:ring-gray-300 focus:ring-2 focus:ring-indigo-500 hover:bg-white transition-all"
                              placeholder="Optional batch number"
                            />
                          </div>
                        </div>

                        {/* Additional fields */}
                        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3 ${formData.procurementType === 'SERVICE' ? 'hidden' : ''}`}>
                          <div>
                            <label className="block text-sm font-semibold text-gray-800 mb-1">
                              Expiry Date
                            </label>
                            <input
                              type="date"
                              value={item.expiryDate}
                              onChange={(e) => handleItemChange(index, 'expiryDate', e.target.value)}
                              className="block w-full rounded-lg border-0 bg-white py-2.5 px-3 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 hover:ring-gray-300 focus:ring-2 focus:ring-indigo-500 transition-all"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-semibold text-gray-800 mb-1">
                              Manufacturing Date
                            </label>
                            <input
                              type="date"
                              value={item.manufacturingDate}
                              onChange={(e) => handleItemChange(index, 'manufacturingDate', e.target.value)}
                              className="block w-full rounded-lg border-0 bg-white py-2.5 px-3 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 hover:ring-gray-300 focus:ring-2 focus:ring-indigo-500 transition-all"
                            />
                          </div>
                        </div>

                        {formData.procurementType === 'FIXED_ASSET' && (
                          <div>
                            <label className="block text-sm font-semibold text-gray-800 mb-1">
                              Serial Numbers
                              <span className="ml-2 text-xs font-normal text-gray-500">
                                one per accepted unit ({String(item.serialNumbersText || '').split(/[\n,]/).map(s => s.trim()).filter(Boolean).length} of {item.acceptedQuantity || 0}) — each unit becomes its own Asset Register record
                              </span>
                            </label>
                            <textarea
                              value={item.serialNumbersText || ''}
                              onChange={(e) => handleItemChange(index, 'serialNumbersText', e.target.value)}
                              className="block w-full rounded-lg border-0 bg-gray-50 py-2.5 px-3 text-sm font-mono text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 hover:ring-gray-300 focus:ring-2 focus:ring-indigo-500 hover:bg-white transition-all resize-y"
                              rows="2"
                              placeholder="One serial number per line (or comma-separated)"
                            />
                          </div>
                        )}

                        <div>
                          <label className="block text-sm font-semibold text-gray-800 mb-1">
                            Item Notes
                          </label>
                          <textarea
                            value={item.notes}
                            onChange={(e) => handleItemChange(index, 'notes', e.target.value)}
                            className="block w-full rounded-lg border-0 bg-gray-50 py-2.5 px-3 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 hover:ring-gray-300 focus:ring-2 focus:ring-indigo-500 hover:bg-white transition-all resize-none"
                            rows="2"
                            placeholder="Any notes specific to this item..."
                          />
                        </div>
                      </div>

                      {/* Available budget of the item's expense account (manual GRN, cost center of the GRN) */}
                      {showBudget && (item.item?._id || item.item) && (() => {
                        const acc = expenseAccountOf(item);
                        const strip = 'mt-2 px-2 py-1 rounded text-[11px] flex flex-wrap items-center gap-x-2';
                        if (!acc) return <p className={`${strip} bg-gray-50 text-gray-500`}>No expense account set on this item — budget not checked</p>;
                        if (!formData.costCenter) return <p className={`${strip} bg-gray-50 text-gray-500`}>Budget · {acc.label}: select the cost center to see the available budget</p>;
                        const b = budgetByAccount[acc.id];
                        if (!b || b.loading) return <p className={`${strip} bg-gray-50 text-gray-500`}>Budget · {acc.label}: loading…</p>;
                        if (b.error) return <p className={`${strip} bg-red-50 text-red-700`}>Budget · {acc.label}: {b.error}</p>;
                        if (b.bypassedValidation && b.availableAmount == null) return <p className={`${strip} bg-gray-50 text-gray-500`}>Budget · {acc.label}: no budget allocation (budget validation is off)</p>;
                        const requested = requestedByAccount[acc.id] || 0;
                        const held = reservedByThisGrn[acc.id] || 0;
                        const over = requested - ((b.availableAmount || 0) + held);
                        return (
                          <p className={`${strip} ${over > 0.005 ? 'bg-amber-50 text-amber-800' : 'bg-green-50 text-green-800'}`}>
                            <span>Budget · <span className="font-medium">{acc.label}</span>:</span>
                            <span>available <span className="font-semibold">{budgetMoney(b.availableAmount)}</span> of {budgetMoney(b.budgetedAmount)}</span>
                            {b.utilizedAmount != null && <span className="text-gray-500">(used {budgetMoney(b.utilizedAmount)})</span>}
                            {held > 0 && <span className="text-indigo-700">· reserved by this GRN {budgetMoney(held)}</span>}
                            {over > 0.005 && <span className="font-semibold">· this GRN needs {budgetMoney(requested)} — {budgetMoney(over)} over</span>}
                          </p>
                        );
                      })()}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Compact Receipt Summary Section */}
            {formData.lineItems.length > 0 && (
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-3 rounded-lg border border-indigo-200 shadow-sm">
                <h3 className="text-xs font-bold text-indigo-800 mb-2 flex items-center">
                  <svg className="w-4 h-4 mr-1 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  Receipt Summary
                </h3>
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-2 mb-2">
                  <div className="bg-white p-2 rounded-lg border border-emerald-100 shadow-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-medium text-slate-600">Total Items</span>
                      <span className="text-sm font-bold text-slate-900">{formData.lineItems.length}</span>
                    </div>
                  </div>
                  <div className="bg-white p-2 rounded-lg border border-indigo-100 shadow-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-medium text-slate-600">Delivered Qty</span>
                      <span className="text-sm font-bold text-indigo-600">
                        {formData.lineItems.reduce((total, item) => total + (item.deliveredQuantity || 0), 0)}
                      </span>
                    </div>
                  </div>
                  <div className="bg-white p-2 rounded-lg border border-indigo-100 shadow-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-medium text-slate-600">Accepted Qty</span>
                      <span className="text-sm font-bold text-indigo-600">
                        {formData.lineItems.reduce((total, item) => total + (item.acceptedQuantity || 0), 0)}
                      </span>
                    </div>
                  </div>
                  <div className="bg-white p-2 rounded-lg border border-indigo-100 shadow-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-medium text-slate-600">Subtotal</span>
                      <span className="text-sm font-bold text-indigo-600">
                        {formData.lineItems.reduce((total, item) => total + ((item.deliveredQuantity || 0) * (item.unitPrice || 0)), 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* SSCL row */}
                <div className="flex items-center gap-3 mt-2">
                  <label className="flex items-center gap-1.5 text-xs font-medium text-gray-700 cursor-pointer select-none">
                    <input type="checkbox" checked={ssclEnabled} onChange={e => setSsclEnabled(e.target.checked)}
                      className="w-3.5 h-3.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                    SSCL
                  </label>
                  {ssclEnabled && parseFloat(ssclRate) > 0 && (
                    <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{ssclRate}%</span>
                  )}
                  {ssclEnabled && <span className="text-xs font-semibold text-gray-800 ml-auto">Rs {ssclAmount.toFixed(2)}</span>}
                </div>

                {/* VAT row */}
                <div className="flex items-center gap-3 mt-1.5">
                  <label className="flex items-center gap-1.5 text-xs font-medium text-gray-700 cursor-pointer select-none">
                    <input type="checkbox" checked={vatEnabled} onChange={e => setVatEnabled(e.target.checked)}
                      className="w-3.5 h-3.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                    VAT
                  </label>
                  {vatEnabled && parseFloat(vatRate) > 0 && (
                    <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{vatRate}%</span>
                  )}
                  {vatEnabled && <span className="text-xs font-semibold text-gray-800 ml-auto">Rs {vatAmount.toFixed(2)}</span>}
                </div>

                {(ssclEnabled || vatEnabled) && (
                  <div className="flex justify-between items-center mt-2 pt-2 border-t border-indigo-200">
                    <span className="text-xs font-bold text-indigo-800">Grand Total</span>
                    <span className="text-sm font-bold text-indigo-700">
                      Rs {(formData.lineItems.reduce((t, i) => t + ((i.deliveredQuantity || 0) * (i.unitPrice || 0)), 0) + ssclAmount + vatAmount).toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Compact Receipt Notes Section */}
            <div className="bg-gradient-to-r from-slate-50 via-gray-50 to-zinc-50 p-3 rounded-lg border border-slate-200 shadow-sm">
              <h3 className="text-xs font-bold text-slate-800 mb-2 flex items-center">
                <svg className="w-4 h-4 mr-1 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Additional Notes
              </h3>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                className="block w-full rounded-lg border-0 bg-white py-2 px-3 text-xs text-gray-900 shadow-sm ring-1 ring-inset ring-slate-200 hover:ring-slate-300 focus:ring-2 focus:ring-indigo-500 hover:bg-gray-50 transition-all resize-none"
                rows="2"
                placeholder="Any additional notes or comments about this delivery..."
              />
            </div>

            {/* Supporting documents: delivery note, invoice, service report… */}
            <ProcurementDocuments ref={docsRef} docType="grn" docId={isEditMode ? grn._id : null} />

            {/* Form Actions */}
            <div className="bg-white px-2 py-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={onCancel}
                  className="px-8 py-3 border border-gray-300 rounded-xl text-base font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors shadow-sm"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-8 py-3 bg-indigo-600 border border-transparent rounded-xl text-base font-medium text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-lg"
                  disabled={loading || formData.lineItems.length === 0}
                >
                  {loading ? (
                    <div className="flex items-center space-x-2">
                      <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>{isEditMode ? 'Updating Receipt...' : 'Creating Receipt...'}</span>
                    </div>
                  ) : (
                    isEditMode ? 'Update Receipt' : 'Create Receipt'
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default GRNForm;